import React, { useState, useEffect } from 'react';
import { userAPI, roleAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, UserX, Trash2, Shield, User, UserCheck, Search, Wrench, ShieldCheck } from 'lucide-react';
import RightCheckbox from '../components/common/RightCheckbox';
import useDarkReady from '../theme/useDarkReady';
import UsersMobile from '../components/users/UsersMobile';
import { useIsAppView } from '../components/users/kit';
// SECONDARY_ROLE_OPTIONS mirrors backend/src/routes/users.js's SECONDARY_ROLES (kept in sync
// manually, short stable enum; see docs/superpowers/specs/2026-09-22-multi-role-login-design.md).
import { saveUser, SITE_ROLE_ELIGIBLE, SECONDARY_ROLE_OPTIONS } from '../utils/userAccess';
// Base-role badge display: label + icon + badge color per role, so a raw enum value like
// 'co-admin' doesn't just fall through to a generic default (and CSS text-transform can't
// be trusted to capitalize both halves of a hyphenated word the same way in every browser).
const ROLE_BADGE = {
  admin: { label: 'Admin', icon: Shield, className: 'badge-danger' },
  'co-admin': { label: 'Co-Admin', icon: ShieldCheck, className: 'badge-catv' },
  agent: { label: 'Agent', icon: UserCheck, className: 'badge-warning' },
  technical: { label: 'Technical', icon: Wrench, className: 'badge-success' },
  staff: { label: 'Staff', icon: User, className: 'badge-info' },
};

function UserModal({ user, roles, onClose, onSuccess }) {
  const [form, setForm] = useState(user
    ? { ...user, siteRoleId: user.siteRole?._id || user.siteRole || '', secondaryRoles: user.secondaryRoles || [] }
    : { name: '', mobile: '', email: '', role: 'staff', password: '', siteRoleId: '', secondaryRoles: [] }
  );
  const [loading, setLoading] = useState(false);
  const [fieldModules, setFieldModules] = useState([]);
  const [fieldRights, setFieldRights] = useState({});

  useEffect(() => {
    userAPI.getModuleRegistry().then(res => {
      const modules = (res.data.data || []).filter(m => m.section === 'FIELD AGENT APP');
      setFieldModules(modules);
      setFieldRights(Object.fromEntries(modules.map(m => [
        m.key,
        Object.fromEntries(m.actions.map(a => [a.key, !!user?.fieldRights?.[m.key]?.[a.key]])),
      ])));
    }).catch(() => {});
  }, [user]);

  const toggleFieldRight = (moduleKey, action) => {
    setFieldRights(r => ({ ...r, [moduleKey]: { ...r[moduleKey], [action]: !r[moduleKey][action] } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await saveUser({ user, form, fieldRights });
      toast.success(user ? 'User updated' : 'User created');
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{user ? 'Edit User' : 'Add Staff User'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile *</label>
                <input className="form-control" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} required />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">{user ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                <input type="password" className="form-control" value={form.password || ''} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!user} minLength={6} placeholder="Min 6 characters" />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-control" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value, secondaryRoles: f.secondaryRoles.filter(r => r !== e.target.value) }))}>
                  <option value="staff">Staff</option>
                  <option value="co-admin">Co-Admin</option>
                  <option value="agent">Agent</option>
                  <option value="technical">Technical</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {form.role === 'admin' && (
              <div style={{ fontSize: '.82rem', color: 'var(--text-muted)', background: 'var(--bg)', padding: 10, borderRadius: 8 }}>
                Admin has full access to everything — no Site Role needed.
              </div>
            )}

            {form.role !== 'admin' && (
              <div className="form-group">
                <label className="form-label">Also log in as</label>
                <div>
                  {SECONDARY_ROLE_OPTIONS.filter(r => r !== form.role).map(r => (
                    <RightCheckbox key={r}
                      checked={form.secondaryRoles.includes(r)}
                      onChange={() => setForm(f => ({
                        ...f,
                        secondaryRoles: f.secondaryRoles.includes(r)
                          ? f.secondaryRoles.filter(x => x !== r)
                          : [...f.secondaryRoles, r],
                      }))}
                      label={ROLE_BADGE[r]?.label || r}
                    />
                  ))}
                </div>
                <p className="text-muted text-small" style={{ marginTop: 4 }}>
                  Grants this login access to those apps too, on top of its main Role above.
                </p>
              </div>
            )}

            {(() => {
              const effectiveRoles = [form.role, ...form.secondaryRoles];
              const showAgentPanel = effectiveRoles.includes('agent');
              const showSiteRolePanel = effectiveRoles.some(r => SITE_ROLE_ELIGIBLE.includes(r) && r !== 'agent');
              return (
                <>
                  {showAgentPanel && (
                    <div className="form-group">
                      <label className="form-label">Agent Permission</label>
                      <div>
                        {fieldModules.map(mod => {
                          const action = mod.actions[0].key;
                          return (
                            <RightCheckbox key={mod.key}
                              checked={!!fieldRights[mod.key]?.[action]}
                              onChange={() => toggleFieldRight(mod.key, action)}
                              label={mod.label.replace(' (Field)', '')}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {showSiteRolePanel && (
                    <div className="form-group">
                      <label className="form-label">Site Role</label>
                      <select className="form-control" value={form.siteRoleId} onChange={e => setForm(f => ({ ...f, siteRoleId: e.target.value }))}>
                        <option value="">— No role assigned (no access) —</option>
                        {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                      </select>
                      <p className="text-muted text-small" style={{ marginTop: 4 }}>
                        Determines exactly what this login can view/add/edit/delete. Manage roles from Site Roles.
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Save User'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersPage() {
  return useIsAppView() ? <UsersMobile /> : <UsersPageDesktop />;
}

function UsersPageDesktop() {
  useDarkReady();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes] = await Promise.all([userAPI.getAll(), roleAPI.getAll()]);
      setUsers(usersRes.data.data);
      setRoles(rolesRes.data.data || []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await userAPI.delete(id);
      toast.success('User deactivated');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleActivate = async (u) => {
    if (!window.confirm(`Activate ${u.name}?`)) return;
    try {
      await userAPI.activate(u._id);
      toast.success('User activated');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleDeletePermanent = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone. Blocked if they have any payment/customer history — deactivate instead in that case.`)) return;
    try {
      await userAPI.deletePermanent(u._id);
      toast.success(`${u.name} permanently deleted`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Staff Users</h2>
          <p className="text-muted text-small">Manage admin and staff accounts</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => navigate('/site-roles')}>
            <ShieldCheck size={15} /> Site Roles
          </button>
          <button className="btn btn-primary" onClick={() => setModal({})}>
            <Plus size={15} /> Add Staff
          </button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <div className="search-input-wrapper">
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Search by name or mobile..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <table>
              <thead><tr><th>Name</th><th>Mobile</th><th>Role</th><th>Rights</th><th>Status</th><th>Last Login</th><th>Actions</th></tr></thead>
              <tbody>
                {users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.mobile?.includes(search)).map(u => (
                  <tr key={u._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar-circle" style={{ width: 32, height: 32, fontSize: '.75rem' }}>{u.name[0]}</div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{u.name}</div>
                          <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{u.email || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: '.85rem' }}>{u.mobile}</td>
                    <td>
                      {(() => {
                        const rb = ROLE_BADGE[u.role] || ROLE_BADGE.staff;
                        const Icon = rb.icon;
                        return (
                          <span className={`badge ${rb.className}`}>
                            <Icon size={10} style={{ display: 'inline', marginRight: 3 }} />
                            {rb.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ fontSize: '.8rem' }}>
                      {u.role === 'agent'
                        ? (() => {
                            const granted = Object.values(u.fieldRights || {}).filter(m => Object.values(m || {}).some(Boolean)).length;
                            return granted > 0
                              ? <span>{granted} field right{granted !== 1 ? 's' : ''} granted</span>
                              : <span className="text-muted">— none —</span>;
                          })()
                        : SITE_ROLE_ELIGIBLE.includes(u.role)
                          ? (u.siteRole?.name || <span className="text-muted">— none —</span>)
                          : <span className="text-muted">—</span>}
                    </td>
                    <td><span className={`badge badge-${u.isActive ? 'success' : 'warning'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                    <td style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>
                      {u.lastLogin ? format(new Date(u.lastLogin), 'dd/MM/yy HH:mm') : 'Never'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => setModal(u)}><Edit2 size={12} /></button>
                        {u.isActive ? (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeactivate(u._id)} title="Deactivate"><UserX size={12} /></button>
                        ) : (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleActivate(u)} title="Activate"><UserCheck size={12} /></button>
                        )}
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeletePermanent(u)} title="Permanently delete">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal !== null && (
        <UserModal
          user={modal?._id ? modal : null}
          roles={roles}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
